import { useId, useState } from "react";
import type { RefObject } from "react";
import { Field } from "../../components";
import styles from "./Auth.module.css";
import { MIN_LONGUEUR, evaluerMotDePasse } from "./MotDePasse";

export type ChampMotDePasseProps = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  /** `new-password` fait proposer un mot de passe par le gestionnaire. */
  autoComplete: "new-password" | "current-password";
  error?: string | null;
  hint?: string;
  /** Affiche la règle de longueur et la jauge : pour un nouveau mot de passe. */
  showRules?: boolean;
  inputRef?: RefObject<HTMLInputElement | null>;
  disabled?: boolean;
};

/**
 * Champ de mot de passe avec bouton « afficher » et, pour un nouveau mot de
 * passe, la règle et une jauge. Le texte de la règle porte l'information ; la
 * jauge est décorative.
 */
export function ChampMotDePasse({
  label,
  value,
  onChange,
  autoComplete,
  error,
  hint,
  showRules = false,
  inputRef,
  disabled,
}: ChampMotDePasseProps) {
  const [visible, setVisible] = useState(false);
  const idRegles = useId();
  const evaluation = evaluerMotDePasse(value);

  return (
    <Field label={label} hint={hint} error={error ?? undefined} required>
      {(commande) => (
        <div className={styles.champMdp}>
          <div className={styles.ligneMdp}>
            <input
              {...commande}
              ref={inputRef}
              type={visible ? "text" : "password"}
              autoComplete={autoComplete}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              disabled={disabled}
              aria-describedby={
                [commande["aria-describedby"], showRules ? idRegles : null].filter(Boolean).join(" ") ||
                undefined
              }
            />
            <button
              type="button"
              className={styles.afficher}
              onClick={() => setVisible((v) => !v)}
              aria-pressed={visible}
              aria-label={visible ? "Masquer le mot de passe" : "Afficher le mot de passe"}
            >
              {visible ? "Masquer" : "Afficher"}
            </button>
          </div>
          {showRules && (
            <div id={idRegles} className={styles.regles}>
              <p className={evaluation.valide ? styles.regleOk : styles.regle}>
                {evaluation.valide ? "✓" : "•"} Au moins {MIN_LONGUEUR} caractères
                <span className="lde-visually-hidden">
                  {evaluation.valide ? " (satisfaite)" : " (non satisfaite)"}
                </span>
              </p>
              <meter
                className={styles.jauge}
                min={0}
                max={4}
                value={evaluation.force}
                aria-label="Robustesse indicative du mot de passe"
              />
              <p className={styles.conseil}>
                Aucun chiffre ni symbole exigé : une phrase longue vaut mieux qu'un mot court compliqué.
              </p>
            </div>
          )}
        </div>
      )}
    </Field>
  );
}
